(() => {

  if (customElements.get('recreation-browser')) return;

  const catalogs = new Map();

  const baseCatalog = (url, element) => {

    try {

      const data = JSON.parse(element.querySelector('[data-metaobject-catalog]')?.textContent || '{}');

      if (Array.isArray(data.types) && data.types.length > 0 && data.types.every(t => typeof t.id === 'string' && Array.isArray(t.makes) && t.makes.every(m => typeof m.id === 'string' && Array.isArray(m.models)))) return Promise.resolve(data);

    } catch { /* Keep the reviewed draft snapshot when records are unavailable. */ }

    if (!catalogs.has(url)) catalogs.set(url, fetch(url).then(r => { if (!r.ok) throw Error(r.status); return r.json(); }).catch(e => { catalogs.delete(url); throw e; }));

    return catalogs.get(url);

  };

  const catalog = async (url, element) => {
    const data = JSON.parse(JSON.stringify(await baseCatalog(url, element)));
    const previewId = element.dataset.configurationPreviewTheme;
    if (previewId && String(window.Shopify?.theme?.id) === previewId && window.Shopify?.theme?.role !== 'main') {
      try {
        const response = await fetch(element.dataset.configurationPreview);
        if (!response.ok) throw Error(response.status);
        const overlay = await response.json();
        data.types.forEach(t => t.makes.forEach(m => m.models.forEach(model => {
          const draft = overlay.models?.[model.id];
          if (Array.isArray(draft)) model.configurations = [...new Map([...(model.configurations || []), ...draft].map(c => [c.id,c])).values()];
        })));
      } catch { /* Reviewed active records still work when a preview snapshot is unavailable. */ }
    }
    return data;
  };
  const configurationURL = (model, root, path) => model?.page_published && model.page_url?.startsWith('/pages/') ? localURL(model.page_url,'',path.join('/')) : localURL(root,'recreation',path.join('/'));
  const groupLabel = kind => ({model_year:'Model year',year_range:'Model years',generation:'Generation',configuration:'Configuration',seating_layout:'Seating layout',floorplan:'Floorplan',trim:'Trim',redesign_checkpoint:'Redesign checkpoint'}[kind] || 'Configuration');
  const yearLabel = item => {
    if (item.year_scope === 'verified_oem_complete_seat_accessory_application') return 'OEM complete-seat accessory application years only: ' + (item.model_years || []).join(', ');
    if (item.year_scope === 'current_page_year_unspecified') return 'Model year not established';
    if (item.year_scope && item.year_scope !== 'single_documented_model_year') return 'Year scope requires review';
    return Array.isArray(item.model_years) && item.model_years.length ? 'Documented model years: ' + item.model_years.join(', ') : 'Model year not established';
  };
  const labels = el => JSON.parse(el.querySelector('[data-labels]').textContent);

  const localURL = (path, view, hash = '') => {

    const url = new URL(path, location.origin);

    if (view) url.searchParams.set('view', view);

    const preview = new URL(location.href).searchParams.get('preview_theme_id');

    if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);

    url.hash = hash;

    return url.pathname + url.search + url.hash;

  };

  const itemURL = (item, root, path) => item?.page_published && typeof item.page_url === 'string' && item.page_url.startsWith('/pages/') ? localURL(item.page_url,'') : localURL(root,'recreation',path);

  const readPath = () => location.hash.slice(1).split('/').filter(Boolean);

  const safeImage = value => {

    if (typeof value !== 'string' || !value.trim()) return null;

    try { const url = new URL(value,location.origin); return url.protocol === 'https:' && (url.origin === location.origin || url.hostname === 'cdn.shopify.com') ? url.href : null; } catch { return null; }

  };

  const resolve = (data, path) => {

    const type = data.types.find(t => t.id === path[0]);

    const make = type?.makes.find(m => m.id === path[1]);

    const model = make?.models.find(m => m.id === path[2]);

    const other = !!type && (path[1] === 'other' || (!!make && path[2] === 'other'));

    const configurations = [];
    let parent = null, invalidConfiguration = path.length > 3 && !model;
    for (const id of path.slice(3)) {
      const child = model?.configurations?.find(c => c.id === id && (c.parent_id || null) === parent && c.model_id === model.id);
      if (!child || configurations.some(c => c.id === id) || configurations.length >= 8) { invalidConfiguration = true; break; }
      configurations.push(child); parent = child.id;
    }
    return {type, make, model, other, configurations, configuration:configurations.at(-1), invalidConfiguration};

  };

  const summary = (state, text) => [text[state.type?.id] || state.type?.name, state.make?.name, state.model?.name || (state.other ? text.other_model : ''), ...(state.configurations || []).map(c => c.name)].filter(Boolean).join(' / ');

  const node = (tag, text, className) => { const el = document.createElement(tag); if (tag === 'a') el.setAttribute('allow-hash-change', ''); if (text) el.textContent = text; if (className) el.className = className; return el; };

  const crumbs = (container, entries) => {

    const list = node('ol', '', 'browse-breadcrumbs__list'); list.setAttribute('role', 'list');

    entries.forEach(({label, href}) => {

      const item = node('li');

      const content = node(href ? 'a' : 'span', label);

      if (href) content.href = href; else content.setAttribute('aria-current', 'page');

      item.append(content); list.append(item);

    });

    container.replaceChildren(list);

  };

  class RecreationBrowser extends HTMLElement {

    connectedCallback() {

      if (this.ready) return;

      this.ready = true;

      this.text = labels(this);

      this.querySelectorAll('a').forEach(a => a.setAttribute('allow-hash-change', ''));

      this.query = s => this.querySelector(s);

      this.query('[data-types]').querySelectorAll('a').forEach(a => a.href = localURL(this.dataset.root, 'recreation', a.dataset.type));

      this.abort = new AbortController();

      const options = {signal: this.abort.signal};

      this.mobile = matchMedia('(max-width: 699px)');

      this.mobile.addEventListener('change', () => this.layoutGrids(), options);

      this.layoutGrids();

      if (this.dataset.landing === 'true') {

        catalog(this.dataset.catalog,this).then(data => { if (this.isConnected) { this.categoryImages(data); this.renderAircraftCredits(data); } }).catch(() => {});

        return;

      }

      this.addEventListener('click', event => {

        const link = event.target.closest('a[href^="#"]');

        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        event.preventDefault();

        event.stopPropagation();

        location.hash = link.getAttribute('href');

      }, options);



      this.query('[data-subtype]').addEventListener('change', () => this.filter(), options);

      this.query('[data-clear]').addEventListener('click', () => { this.query('[data-subtype]').value = ''; this.filter(); this.query('[data-subtype]').focus(); }, options);

      window.addEventListener('hashchange', () => this.render(true), options);

      this.query('[data-browser]').hidden = false;

      this.query('[data-status]').textContent = this.text.loading;

      catalog(this.dataset.catalog,this).then(data => { if (!this.isConnected) return; this.data = data; this.categoryImages(data); this.renderAircraftCredits(data); this.render(false); }).catch(() => { if (this.isConnected) this.query('[data-status]').textContent = this.text.error; });

    }

    disconnectedCallback() { this.abort?.abort(); this.ready = false; }

    render(focus) {

      if (!this.data) return;

      const path = readPath();

      if (!path.length && this.dataset.pagePath) path.push(...this.dataset.pagePath.split('/'));

      if (!path.length && this.dataset.pageType) path.push(this.dataset.pageType);

      const visible = [...this.querySelectorAll('[data-type]')].map(e => e.dataset.type);

      let state = resolve(this.data, path);

      if (state.type && !visible.includes(state.type.id)) state = {};

      this.state = state;

      const {type, make, model, other, configurations = [], configuration, invalidConfiguration} = state;

      const selected = !!(model || other);
      const credits = this.query('[data-aircraft-credits]');
      if (credits) credits.hidden = !!type && type.id !== 'aviation';

      this.query('[data-types]').hidden = !!type;

      this.query('[data-browser]').hidden = !type;

      const entries = [{label:this.text.home,href:localURL(this.dataset.root,'')},{label:this.text.recreation,href:type ? localURL(this.dataset.root,'recreation') : null}];

      if (type) entries.push({label:this.text[type.id] || type.name,href:make || selected ? itemURL(type,this.dataset.root,type.id) : null});

      if (make) entries.push({label:make.name,href:selected ? itemURL(make,this.dataset.root,[type.id,make.id].join('/')) : null});

      if (selected) entries.push({label:model?.name || this.text.other_model,href:configurations.length || invalidConfiguration ? itemURL(model,this.dataset.root,[type.id,make.id,model.id].join('/')) : null});
      configurations.forEach((c,i) => entries.push({label:c.name,href:i < configurations.length-1 ? configurationURL(model,this.dataset.root,[type.id,make.id,model.id,...configurations.slice(0,i+1).map(x=>x.id)]) : null}));
      if (invalidConfiguration) entries.push({label:'Selection unavailable'});

      crumbs(this.query('[data-crumbs]'),entries);

      if (!type) { this.layoutGrids(); return; }

      this.path = [type.id, make?.id, model?.id || (other ? 'other' : ''), ...configurations.map(c=>c.id)].filter(Boolean);

      this.query('[data-title]').textContent = selected ? (type.id === 'aviation' && model ? model.name : this.text.selected) : (make?.name || this.text[type.id] || type.name);

      this.query('[data-intro]').textContent = selected ? '' : (type.pending ? this.text.pending : (make ? this.text.choose_model : this.text.choose_make));

      this.query('[data-road]').hidden = !['atv','utv'].includes(type.id);

      this.query('[data-filters]').hidden = selected;

      this.query('[data-grid]').hidden = selected;

      this.query('[data-other]').hidden = selected;

      this.query('[data-project]').hidden = !selected;

      this.query('[data-empty]').hidden = true;

      this.query('[data-status]').textContent = '';

      this.renderAircraftReference(type.id === 'aviation' ? model : null);

      this.query('[data-other]').href = '#' + [type.id, make?.id, 'other'].filter(Boolean).join('/');

      if (selected) {

        this.query('[data-selection]').textContent = summary(state, this.text);

        const hero = this.query('[data-selected-image]');

        if (hero) { hero.replaceChildren(); hero.hidden = !model?.images || !!configuration || invalidConfiguration; if (model?.images && !configuration && !invalidConfiguration) { this.addImage(hero,model); document.dispatchEvent(new CustomEvent('baesic:vehicle-images-added')); } }

        this.renderConfigurations(model, configuration, invalidConfiguration);
        this.renderProducts(invalidConfiguration ? null : model, configuration);

        this.query('[data-note]').textContent = this.text[{'boats':'boat_note','pwc':'pwc_note','rvs':'rv_note','golf-carts':'golf_note','aviation':'aviation_note'}[type.id]] || this.text.details;

        this.query('[data-quote]').hidden = invalidConfiguration;
        this.query('[data-quote]').href = localURL(this.dataset.root + 'pages/contact-us', 'recreation-quote', this.path.join('/'));

      } else {



        this.items = (make ? make.models : type.makes).filter(item => type.id !== 'aviation' || item.featured !== false);

        const subtypes = [...new Set((make ? make.models : type.makes.flatMap(m => m.models)).filter(m => type.id !== 'aviation' || m.featured !== false).flatMap(m => m.subtypes))].sort();

        const select = this.query('[data-subtype]'); select.replaceChildren(new Option(this.text.all, ''));

        subtypes.forEach(s => select.add(new Option(this.text[s] || s, s)));

        this.filter();

      }

      if (focus) { this.query('[data-title]').focus({preventScroll:true}); this.query('[data-crumbs]').scrollIntoView({block:'start',behavior:'instant'}); }

    }

    renderAircraftCredits(data) {
      const panel = this.query('[data-aircraft-credits]'), list = this.query('[data-aircraft-credit-list]');
      if (!panel || !list) return;
      list.replaceChildren();
      const aircraft = data.types.find(type => type.id === 'aviation');
      (aircraft?.makes || []).flatMap(make => make.models).filter(model => model.featured !== false && model.image_provenance).forEach(model => {
        const credit = model.image_provenance, row = node('p');
        row.append(node('span', `${model.name}: ${credit.attribution || ''} `));
        for (const [label, href] of [[this.text.aircraft_photo_source, credit.source_url], [credit.license, credit.license_url]]) {
          try { const url = new URL(href); if (url.protocol !== 'https:') continue;
            const link = node('a', label, 'link'); link.href = url.href; link.target = '_blank'; link.rel = 'noopener noreferrer'; row.append(link, document.createTextNode(' '));
          } catch {}
        }
        list.append(row);
      });
      panel.hidden = !list.childElementCount;
    }
    renderAircraftReference(model) {
      const panel = this.query('[data-aircraft-reference]');
      if (!panel) return;
      panel.replaceChildren(); panel.hidden = !model?.description;
      if (!model?.description) return;
      panel.append(node('h3', this.text.aircraft_reference, 'h4'), node('p', model.description));
      if (model.generation_year_note) panel.append(node('p', model.generation_year_note));
      if (model.reference_specs && Object.keys(model.reference_specs).length) {
        const list = node('dl', '', 'rec-configuration-specs');
        Object.entries(model.reference_specs).forEach(([key, value]) => list.append(node('dt', key.replaceAll('_', ' ')), node('dd', String(value))));
        panel.append(list);
      }
      if (model.variant_cautions?.length) {
        const list = node('ul'); model.variant_cautions.forEach(text => list.append(node('li', text))); panel.append(list);
      }
      panel.append(node('p', this.text.aircraft_fitment, 'rec-fitment-status'));
      const sources = node('details'); sources.append(node('summary', this.text.aircraft_sources));
      (model.source_urls || []).forEach((source, i) => {
        try {
          const url = new URL(source); if (url.protocol !== 'https:') return;
          const link = node('a', `${this.text.aircraft_source} ${i + 1}: ${url.hostname}`, 'link');
          link.href = url.href; link.target = '_blank'; link.rel = 'noopener noreferrer';
          const row = node('p'); row.append(link); sources.append(row);
        } catch {}
      });
      if (model.image_provenance?.attribution) sources.append(node('p', model.image_provenance.attribution));
      panel.append(sources);
    }
    renderConfigurations(model, configuration, invalid) {
      const panel = this.query('[data-configurations]'), detail = this.query('[data-configuration-detail]');
      if (!panel || !detail) return;
      panel.replaceChildren(); detail.replaceChildren(); panel.hidden = true; detail.hidden = !configuration && !invalid;
      if (invalid) { detail.append(node('p','This configuration is unavailable. Return to the model to choose a documented selection.')); return; }
      if (!model) return;
      const children = (model.configurations || []).filter(c => c.model_id === model.id && (c.parent_id || null) === (configuration?.id || null));
      if (children.length) {
        panel.hidden = false; panel.append(node('h3',configuration ? 'Choose a more specific configuration' : 'Documented years, layouts and trims','h4'));
        panel.append(node('p','These groups describe documented configurations. They do not establish upholstery or product interchangeability.','text-subdued'));
        const list = node('div','','rec-configuration-list');
        children.sort((a,b)=>(a.sort_order||0)-(b.sort_order||0)).forEach(c=>{
          const a=node('a','','rec-configuration-card'); a.dataset.configurationChoice=c.id;
          a.href=configurationURL(model,this.dataset.root,[...this.path,c.id]);
          a.append(node('strong',c.name),node('span',groupLabel(c.group_type),'text-subdued'),node('small',yearLabel(c)));
          list.append(a);
        }); panel.append(list);
      }
      if (!configuration) return;
      detail.append(node('h3',configuration.name,'h4'),node('p',yearLabel(configuration)));
      if (configuration.year_scope === 'verified_oem_complete_seat_accessory_application') detail.append(node('p','These years apply only to the named OEM complete accessory seat and listed trims. They do not define a vehicle generation or establish compatibility for custom upholstery, covers, foam or hardware.'));
      if (configuration.market) detail.append(node('p','Market: '+configuration.market));
      detail.append(node('p',configuration.compatibility_status === 'verified_for_explicit_products' ? 'Only explicitly assigned products have been reviewed for this configuration.' : 'Fitment unverified. Confirm the exact vehicle and upholstery before ordering.','rec-fitment-status'));
      const dl=node('dl','','rec-configuration-specs');
      Object.entries(configuration.configuration || {}).forEach(([key,value])=>{dl.append(node('dt',key.replaceAll('_',' ')),node('dd',Array.isArray(value) ? value.join(', ') : typeof value==='object' ? JSON.stringify(value) : String(value)));});
      detail.append(dl);
      if (configuration.evidence_summary) detail.append(node('p',configuration.evidence_summary));
      if (configuration.notes) detail.append(node('p',configuration.notes,'text-subdued'));
      const evidence=node('details'); evidence.append(node('summary','Sources and verification'));
      evidence.append(node('p',configuration.verification_status === 'verified_configuration_not_fitment' ? 'Configuration documented; compatibility not established by this research.' : 'Configuration evidence awaiting review.'));
      if(configuration.verified_at) evidence.append(node('p','Checked: '+configuration.verified_at));
      (configuration.source_urls || []).forEach((source,i)=>{try{const url=new URL(source);if(url.protocol!=='https:')return;const a=node('a','Source '+(i+1),'link');a.href=url.href;a.target='_blank';a.rel='noopener noreferrer';evidence.append(a);}catch{}});
      detail.append(evidence);
    }
    async renderProducts(model, configuration) {

      const panel = this.query('[data-recreation-products]');

      if (!panel) return;

      const token = this.productRequest = (this.productRequest || 0) + 1;

      panel.hidden = !model;

      if (!model) return;

      const grid = this.query('[data-product-grid]'), status = this.query('[data-product-status]');

      grid.replaceChildren(); status.textContent = 'Loading assigned products.';

      try {

        if (!this.productCatalog) this.productCatalog = (async () => {

          const products = []; let page = 1;

          while (page) {

            const url = new URL(localURL(this.dataset.root + 'collections/all','recreation-products'),location.origin);

            url.searchParams.set('page',String(page));

            const response = await fetch(url);

            if (!response.ok) throw Error(response.status);

            const data = await response.json(); products.push(...data.products);

            const next = Number(data.next_page); if (next && next <= page) throw Error('Invalid product pagination');

            page = next || null;

          }

          return products;

        })().catch(error => { this.productCatalog = null; throw error; });

        const products = await this.productCatalog;

        if (token !== this.productRequest || this.state.model?.id !== model.id) return;

        const key = configuration ? 'configurations' : 'models';
        const selectedId = configuration?.metaobject_id || model.metaobject_id;
        const matched = products.filter(p => Array.isArray(p[key]) && p[key].some(id => String(id).split('/').pop() === String(selectedId).split('/').pop()));

        matched.forEach(product => {

          const url = new URL(product.url,location.origin);

          if (url.origin !== location.origin || !url.pathname.startsWith('/products/')) return;

          const link = node('a',product.title,'button button--outline'); link.href = localURL(url.pathname,''); grid.append(link);

        });

        status.textContent = matched.length ? '' : 'No products are assigned to this selection yet. Request a quote below.';

      } catch { if (token === this.productRequest) status.textContent = 'Product availability could not be loaded. Request a quote below.'; }

    }

    filter() {

      if (!this.items || !this.state?.type) return;



      const subtype = this.query('[data-subtype]').value;

      const {type, make} = this.state;

      const matches = this.items.filter(item => {

        const models = make ? [item] : item.models;

        return models.some(m => !subtype || m.subtypes.includes(subtype));

      });

      const grid = this.query('[data-grid]'); grid.replaceChildren();

      matches.forEach(item => {

        const a = node('a', '', 'rec-card collection-card group'); a.href = item.page_published ? itemURL(item,this.dataset.root,[type.id,make?.id,item.id].filter(Boolean).join('/')) : '#' + [type.id, make?.id, item.id].filter(Boolean).join('/'); a.dataset.choice = item.id;

        const media = node('div', '', 'content-over-media');

        const frame = node('div', '', 'image-wrapper rec-placeholder'); frame.setAttribute('aria-hidden','true');

        if (make) {

          const icon = this.query(`[data-type="${type.id}"] svg`)?.cloneNode(true);

          if (icon) frame.append(icon);

        } else frame.append(node('span',item.name,'rec-make-name'));

        this.addImage(frame,item);

        const content = node('div', '', 'collection-card__content color-scheme text-center');

        content.append(node('span', item.name, 'button'));

        if (make) {

          content.append(node('small', this.text[item.catalog_level] || item.catalog_level, 'rec-card-meta'));

          content.append(node('span', item.subtypes.map(s => this.text[s] || s).join(' Aï¿½ '), 'text-subdued rec-card-meta'));

          content.append(node('small', item.model_year ? `${this.text.year}: ${item.model_year}` : this.text.unspecified, 'text-subdued rec-card-meta'));

        } else content.append(node('span', this.text.choose_model, 'text-subdued rec-card-meta'));

        media.append(frame,content); a.append(media);

        grid.append(a);

      });

      this.query('[data-status]').textContent = `${matches.length} ${this.text.results}`;

      this.query('[data-empty]').hidden = matches.length > 0;

      this.layoutGrids();

      if (grid.querySelector('[data-vehicle-image-key]')) document.dispatchEvent(new CustomEvent('baesic:vehicle-images-added'));

    }

    addImage(frame,item) {

    const day = safeImage(item.images?.day?.url), night = safeImage(item.images?.night?.url);

    if (day || night) {

      frame.removeAttribute('aria-hidden');

      if (item.image_presentation?.kind === 'logo') { frame.classList.add('rec-logo-frame'); frame.dataset.logoPlate = ['dark','light','neutral'].includes(item.image_presentation.plate) ? item.image_presentation.plate : 'neutral'; }

      [...frame.children].forEach(child => child.setAttribute('aria-hidden','true'));

      const image = node('img','','primary-image rec-photo');

      if (item.image_provenance?.generated) { image.style.padding = '6%'; image.style.boxSizing = 'border-box'; }

      const initial = document.documentElement.dataset.siteMode === 'night' ? (night || day) : (day || night);

      image.alt = (initial === night ? item.images?.night?.alt : item.images?.day?.alt) || item.name;

      image.loading = 'lazy'; image.decoding = 'async'; image.draggable = false;

      image.dataset.vehicleImageKey = `recreation:${item.id}`;

      image.dataset.vehicleDaySrc = day || ''; image.dataset.vehicleNightSrc = night || '';

      const dimensions = [item.images?.day,item.images?.night].filter(v => Number(v?.width)>0 && Number(v?.height)>0);

      const ratio = dimensions.length ? Math.min(...dimensions.map(v=>Number(v.width)/Number(v.height))) : 1.5;

      frame.style.setProperty('--vehicle-image-ratio',item.image_presentation?.kind === 'logo' ? 1.5 : ratio);

      image.width = 600; image.height = Math.round(600/ratio);

      image.addEventListener('load',()=>{

        image.alt = (image.dataset.vehicleModeReady === 'night' ? item.images?.night?.alt : item.images?.day?.alt) || item.name;

        if (!dimensions.length && image.naturalWidth && image.naturalHeight) frame.style.setProperty('--vehicle-image-ratio',image.naturalWidth/image.naturalHeight);

        document.dispatchEvent(new CustomEvent('baesic:vehicle-ratios'));

      });

      // The shared mode loader selects responsive sources after grid layout.
      frame.append(image);

    }

    }

    categoryImages(data) {

      data.types.forEach(type => {

        const link = this.query(`[data-type="${type.id}"]`);

        if (link) link.href = itemURL(type,this.dataset.root,type.id);

        const frame = this.query(`[data-type="${type.id}"] .image-wrapper`);

        if (frame && !frame.querySelector("img.rec-photo")) this.addImage(frame,type);

      });

      if (this.query("[data-types] [data-vehicle-image-key]")) document.dispatchEvent(new CustomEvent("baesic:vehicle-images-added"));

    }

    layoutGrids() {

      this.querySelectorAll('.collection-list').forEach(grid => {

        const cards = [...grid.children].filter(card => !card.hidden);

        const configured = Number(this.mobile.matches ? this.dataset.mobileColumns : this.dataset.columns) || (this.mobile.matches ? 2 : 5);

        const columns = Math.max(1,Math.min(configured,cards.length));

        grid.style.setProperty('--nav-columns',columns);

        grid.style.maxWidth = `calc(${columns} * 420px + ${columns-1} * var(--nav-gap))`;

        cards.forEach(card => card.style.gridColumn = 'auto / span 2');

        const remainder = cards.length % columns;

        if (remainder) cards[cards.length-remainder].style.gridColumn = `${columns-remainder+1} / span 2`;

      });

    }

  }

  class RecreationContext extends HTMLElement {

    connectedCallback() {

      const text = labels(this);

      const back = this.querySelector('[data-back]'); back.setAttribute('allow-hash-change', ''); back.href = localURL(this.dataset.root, 'recreation', location.hash.slice(1));

      catalog(this.dataset.catalog,this).then(data => {

        if (!this.isConnected) return;

        const state = resolve(data, readPath());

        const selection = state.invalidConfiguration ? 'Configuration unavailable â€” confirm your selection' : state.type && (state.model || state.other) ? summary(state, text) : text.other_model;

        const entries = [{label:text.home,href:localURL(this.dataset.root,'')},{label:text.recreation,href:localURL(this.dataset.root,'recreation')}];

        if (state.type) entries.push({label:text[state.type.id] || state.type.name,href:localURL(this.dataset.root,'recreation',state.type.id)});

        if (state.make) entries.push({label:state.make.name,href:localURL(this.dataset.root,'recreation',[state.type.id,state.make.id].join('/'))});

        if (state.model || state.other) entries.push({label:state.model?.name || text.other_model,href:state.model ? itemURL(state.model,this.dataset.root,[state.type.id,state.make.id,state.model.id].join('/')) : back.href});
        (state.configurations || []).forEach((c,i)=>entries.push({label:c.name,href:configurationURL(state.model,this.dataset.root,[state.type.id,state.make.id,state.model.id,...state.configurations.slice(0,i+1).map(x=>x.id)])}));

        entries.push({label:text.inquiry}); crumbs(this.querySelector('[data-crumbs]'),entries);

        this.querySelector('[data-selection]').textContent = selection;

        const body = document.querySelector('form[action*="contact"] [name="contact[body]"]');

        if (body && !body.value.trim()) { body.value = `${text.selected}: ${selection}\n\n`; body.dispatchEvent(new Event('input',{bubbles:true})); }

      }).catch(() => { this.querySelector('[data-selection]').textContent = text.error; });

    }

  }

  customElements.define('recreation-browser', RecreationBrowser);

  customElements.define('recreation-context', RecreationContext);

})();






